import L from 'leaflet';

/**
 * Leert Leaflet een driehoek tekenen op het canvas.
 *
 * Waarom deze omweg. De stippen worden op een canvas getekend, en dat is met
 * opzet: daarom staan ze bij elke zoombeweging exact op hun plek. Losse
 * elementen per woning deden dat niet — die verspringen zichtbaar zodra een
 * zoomanimatie klaar is.
 *
 * Leaflet kent alleen cirkels op dat canvas. In plaats van de tekenwijze om
 * te gooien, leren we de bestaande tekenaar er één vorm bij. Een CircleMarker
 * met `driehoek: true` in zijn opties wordt voortaan als driehoek getekend;
 * al het andere — positie, straal, kleuren, klikken — blijft werken zoals het
 * werkte.
 *
 * Dit grijpt in op een deel van Leaflet dat niet als officiële ingang bedoeld
 * is. Bij een grote versiesprong van Leaflet is dit dus het eerste om na te
 * kijken. De afweging: dat risico is kleiner dan het terugbrengen van de
 * verspringende stippen.
 */

/**
 * De extra optie bekend maken bij Leaflets eigen typen, zodat een
 * CircleMarker hem gewoon accepteert.
 */
declare module 'leaflet' {
  interface PathOptions {
    /** Teken deze marker als driehoek in plaats van als cirkel. */
    driehoek?: boolean;
  }
}

/** Leaflet-typen die we hier nodig hebben maar die niet publiek zijn. */
interface CanvasIntern {
  _ctx: CanvasRenderingContext2D;
  _fillStroke(ctx: CanvasRenderingContext2D, laag: unknown): void;
}

interface CirkelIntern {
  _empty(): boolean;
  _point: { x: number; y: number };
  _radius: number;
  options: { driehoek?: boolean };
}

/**
 * Een driehoek met dezelfde optische zwaarte als de cirkel die hij vervangt.
 *
 * Een driehoek met straal r oogt kleiner dan een cirkel met straal r, omdat
 * er meer lucht in de hoeken zit. Met factor 1,4 komt het oppervlak ongeveer
 * overeen en staan koop en huur naast elkaar even zwaar op de kaart.
 */
const SCHAAL = 1.4;

let aangezet = false;

/** Eén keer aanroepen, voordat de kaart getekend wordt. */
export function zetDriehoekAan(): void {
  if (aangezet) return;
  aangezet = true;

  const canvas = L.Canvas.prototype as unknown as Record<string, unknown>;
  const origineel = canvas._updateCircle as (laag: unknown) => void;

  canvas._updateCircle = function (this: CanvasIntern, laag: unknown): void {
    const cirkel = laag as CirkelIntern;

    // Alles zonder de vlag blijft een gewone cirkel.
    if (!cirkel.options.driehoek) {
      origineel.call(this, laag);
      return;
    }

    if (cirkel._empty()) return;

    const { x, y } = cirkel._point;
    const straal = cirkel._radius * SCHAAL;
    const halveBasis = straal * 0.866; // sin(60°), voor een gelijkzijdige driehoek
    const onder = straal * 0.5;

    const ctx = this._ctx;
    ctx.beginPath();
    ctx.moveTo(x, y - straal);
    ctx.lineTo(x + halveBasis, y + onder);
    ctx.lineTo(x - halveBasis, y + onder);
    ctx.closePath();

    // Laat Leaflet zelf de vulling, de rand en de doorzichtigheid zetten, zodat
    // een driehoek precies dezelfde opties begrijpt als een cirkel.
    this._fillStroke(ctx, laag);
  };
}
